/**
 * Support lookup: rows by ref_user_id or eligible_participant_id (paginated).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import {
  fetchPaperJournalEpidRowsThunk,
  fetchPaperJournalUserRowsThunk,
  PAPER_JOURNAL_LOOKUP_PAGE_SIZE_OPTIONS,
  PaperJournalLookupPageSizeOption,
  selectPaperJournalLookupCurrentPage,
  selectPaperJournalLookupError,
  selectPaperJournalLookupPageSize,
  selectPaperJournalLookupRows,
  selectPaperJournalLookupStatus,
  selectPaperJournalLookupTotal,
  selectPaperJournalLookupTotalPages,
  setLookupPageSize,
  clearLookupRows,
} from '@/app/store/paperJournalSlice';
import { Input, RadioGroup } from '@/components/common/FormField';
import { Button } from '@/components/common/Modal';
import { Pagination } from '@/components/common/Table';
import { PaperJournalExpandableRowList } from '@/pages/PaperJournal/PaperJournalExpandableRowList/PaperJournalExpandableRowList';

import './PaperJournalRowsLookup.scss';

type LookupTab = 'user' | 'epid';

const LOOKUP_MODE_OPTIONS = [
  { value: 'user', label: 'By ref user' },
  { value: 'epid', label: 'By EPID' },
];

interface PaperJournalRowsLookupProps {
  embedded?: boolean;
}

export const PaperJournalRowsLookup: React.FC<PaperJournalRowsLookupProps> = ({
  embedded = false,
}) => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectPaperJournalLookupRows);
  const total = useAppSelector(selectPaperJournalLookupTotal);
  const status = useAppSelector(selectPaperJournalLookupStatus);
  const error = useAppSelector(selectPaperJournalLookupError);
  const currentPage = useAppSelector(selectPaperJournalLookupCurrentPage);
  const pageSize = useAppSelector(selectPaperJournalLookupPageSize);
  const totalPages = useAppSelector(selectPaperJournalLookupTotalPages);

  const [tab, setTab] = useState<LookupTab>('user');
  const [userInput, setUserInput] = useState('');
  const [epidInput, setEpidInput] = useState('');
  const [activeUserId, setActiveUserId] = useState<number | null>(null);
  const [activeEpid, setActiveEpid] = useState<string | null>(null);

  useEffect(() => {
    dispatch(clearLookupRows());
  }, [dispatch, tab]);

  const loading = status === 'loading';
  const failed = status === 'failed';

  const runUserSearch = useCallback(() => {
    const id = Number.parseInt(userInput.trim(), 10);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    setActiveUserId(id);
    setActiveEpid(null);
    void dispatch(
      fetchPaperJournalUserRowsThunk({
        refUserId: id,
        limit: pageSize,
        offset: 0,
      }),
    );
  }, [dispatch, pageSize, userInput]);

  const runEpidSearch = useCallback(() => {
    const token = epidInput.trim();
    // Accept decimal-numeric and tenant-hash EPIDs; never invent / search "0".
    if (!token || token === '0') {
      return;
    }
    setActiveEpid(token);
    setActiveUserId(null);
    void dispatch(
      fetchPaperJournalEpidRowsThunk({
        eligibleParticipantId: token,
        limit: pageSize,
        offset: 0,
      }),
    );
  }, [dispatch, epidInput, pageSize]);

  const goToPage = useCallback(
    (page: number) => {
      const offset = (page - 1) * pageSize;
      if (activeUserId !== null) {
        void dispatch(
          fetchPaperJournalUserRowsThunk({
            refUserId: activeUserId,
            limit: pageSize,
            offset,
          }),
        );
      } else if (activeEpid !== null) {
        void dispatch(
          fetchPaperJournalEpidRowsThunk({
            eligibleParticipantId: activeEpid,
            limit: pageSize,
            offset,
          }),
        );
      }
    },
    [activeEpid, activeUserId, dispatch, pageSize],
  );

  const changePageSize = useCallback(
    (size: number) => {
      if (!PAPER_JOURNAL_LOOKUP_PAGE_SIZE_OPTIONS.includes(size as PaperJournalLookupPageSizeOption)) {
        return;
      }
      dispatch(setLookupPageSize(size as PaperJournalLookupPageSizeOption));
      const offset = 0;
      if (activeUserId !== null) {
        void dispatch(
          fetchPaperJournalUserRowsThunk({
            refUserId: activeUserId,
            limit: size,
            offset,
          }),
        );
      } else if (activeEpid !== null) {
        void dispatch(
          fetchPaperJournalEpidRowsThunk({
            eligibleParticipantId: activeEpid,
            limit: size,
            offset,
          }),
        );
      }
    },
    [activeEpid, activeUserId, dispatch],
  );

  const handleTabChange = (value: string) => {
    if (value === 'user' || value === 'epid') {
      setTab(value);
    }
  };

  return (
    <div
      className={`paper-journal-rows-lookup${embedded ? ' paper-journal-rows-lookup--embedded' : ''}`}
    >
      {!embedded && (
        <header className="paper-journal-rows-lookup__header">
          <div>
            <h1 className="paper-journal-rows-lookup__title">Paper journal row lookup</h1>
            <p className="paper-journal-rows-lookup__lede">
              Query durable rows by internal user id or eligible participant id.
            </p>
          </div>
          <Link className="paper-journal-rows-lookup__back" to="/paper-journal">
            ← Paper Journal
          </Link>
        </header>
      )}

      <RadioGroup
        name="pj-lookup-mode"
        label="Search by"
        value={tab}
        onChange={handleTabChange}
        options={LOOKUP_MODE_OPTIONS}
      />

      {tab === 'user' && (
        <div className="paper-journal-rows-lookup__form">
          <div className="paper-journal-rows-lookup__form-row">
            <Input
              id="pj-lookup-user"
              label="Ref user id"
              type="number"
              value={userInput}
              onChange={setUserInput}
              placeholder="e.g. 12345"
            />
            <Button onClick={runUserSearch}>Search</Button>
          </div>
        </div>
      )}

      {tab === 'epid' && (
        <div className="paper-journal-rows-lookup__form">
          <div className="paper-journal-rows-lookup__form-row">
            <Input
              id="pj-lookup-epid"
              label="Eligible participant id"
              type="text"
              value={epidInput}
              onChange={setEpidInput}
              placeholder="numeric or tenant hash"
            />
            <Button onClick={runEpidSearch}>Search</Button>
          </div>
        </div>
      )}

      {failed && error !== null && (
        <div className="paper-journal-rows-lookup__error" role="alert">
          {error}
        </div>
      )}

      <PaperJournalExpandableRowList
        rows={rows}
        loading={loading}
        emptyMessage="Run a search to load rows."
      />

      {rows.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          itemsPerPage={pageSize}
          onPageChange={goToPage}
          pageSizeOptions={[...PAPER_JOURNAL_LOOKUP_PAGE_SIZE_OPTIONS]}
          onPageSizeChange={changePageSize}
        />
      )}
    </div>
  );
};

export default PaperJournalRowsLookup;
